import {
  Box,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  useMediaQuery,
  Card,
  CardContent,
  Grid,
  Tooltip,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import DynamicForm from "../../UI/DynamicForm";
import TemplateSearchDropdown from "../../UI/TemplateSearchDropdown";
import { getContractTitles } from "../../../actions/contractActions";
import TitleInputDialog from "../../../dialogs/TitleInputDialog";
import { showInsufficientCreditsModal } from "../../../actions/creditActions";
import { deductCredits } from "../../../utils/HelperFunction/deductCredits";
import RegenerateConfirmationDialog from "../../../dialogs/RegenerateConfirmationDialog";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBarsStaggered } from "@fortawesome/free-solid-svg-icons";
import {
  Send,
  Search,
  FileText,
  Clock,
  ArrowRight,
  Palette,
} from "lucide-react";
import {
  generateDocumentStreamApi,
  getContractTypesApi,
  getDocumentSchemaApi,
} from "../../../services/api/documentGenerationApi";
import {
  saveDraftingHistoryApi,
  updateDraftingHistoryDocumentApi,
  getDraftingHistoryApi,
} from "../../../services/api/draftingHistoryApi";
import { parseSSEStream } from "../../../utils/HelperFunction/utils";

const ContractForm = ({
  isCollapsed,
  toggleSidebar,
  setGeneratedContract,
  isGenerating,
  setIsGenerating,
  readOnly,
  setReadOnly,
  isEditing,
  setIsEditing,
}) => {
  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down("lg"));

  const dispatch = useDispatch();

  const credits = useSelector((state) => state.credit.credits); // Get credits from Redux store
  const userId = useSelector((state) => state.credit.userId);

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const selectedContract = useSelector((state) => state.selectedContract);
  const { selectedApp } = selectedContract;

  const newContractRequest = useSelector(
    (state) => state.newContractRequest
  );
  const { isClicked } = newContractRequest;

  const initializeFormData = (fields) => {
    const data = {};

    fields.forEach((field) => {
      if (field.type === "list" && Array.isArray(field.fields)) {
        // Add each nested field to this object
        field.fields.forEach((nestedField) => {
          data[nestedField.name] = nestedField.value || "";
        });
      } else {
        // For non-list fields, assign default value or empty string
        data[field.name] = field.value || "";
      }
    });

    return data;
  };

  const [selectedContractType, setSelectedContractType] = useState("");
  const [formConfig, setFormConfig] = useState({});
  const [contractTypes, setContractTypes] = useState([]);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [isLoadingTypes, setIsLoadingTypes] = useState(false);
  const [openTitleInputModal, setOpenTitleInputModal] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [tempChatId, setTempChatId] = useState("");
  const [formData, setFormData] = useState(() => {
    return initializeFormData(formConfig?.form?.fields || []);
  });

  const handleSaveTitleInput = async (title, isConfirmed, editing = false) => {
    if (!title) return;

    if ((!isConfirmed && selectedApp) || editing) {
      setOpenConfirmationModal(true);
      return;
    }
    setOpenConfirmationModal(false);

    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal()); // Trigger modal if credits are insufficient
      return;
    }

    setIsGenerating(true);
    setReadOnly(true);
    const newChatId = tempChatId ? tempChatId : uuidv4();
    const currentTime = dayjs().format("DD-MM-YYYY");
    const date = formData.Date;
    let updatedFormData;
    if (date) {
      // Parse the date using dayjs
      const day = dayjs(date).date().toString();
      const year = dayjs(date).year().toString();
      const monthName = dayjs(date).format("MMMM");

      updatedFormData = {
        ...formData,
        user_id: userInfo._id,
        chat_id: selectedApp ? selectedApp?.chat_id : newChatId,
        timecreated: currentTime,
        Category: selectedApp
          ? selectedApp?.Category
          : selectedContractType.Name,
        Title: title,
        Day: day,
        Month: monthName,
        Year: year,
      };
    } else {
      updatedFormData = {
        ...formData,
        user_id: userInfo._id,
        chat_id: selectedApp ? selectedApp?.chat_id : newChatId,
        timecreated: currentTime,
        Category: selectedApp
          ? selectedApp?.Category
          : selectedContractType.Name,
        Title: title,
      };
    }
    const contractName = selectedApp
      ? selectedApp?.Category
      : selectedContractType.Name;
    setTempChatId(newChatId);
    try {
      const readableStream = await generateDocumentStreamApi(
        contractName,
        updatedFormData
      );
      let html = "";
      let metadata = {};
      await parseSSEStream(readableStream, ({ type, data }) => {
        if (type === "html") html = data.html;
        if (type === "done") metadata = data.metadata || {};
      });

      const filledForm = metadata.filled_form;
      const generatedContract = {
        html,
        metadata,
        form_metadata: filledForm
          ? { id: filledForm.title, fields: filledForm.fields }
          : undefined,
      };

      const chatId = selectedApp ? selectedApp?.chat_id : newChatId;
      let savedHistoryId = selectedApp?._id;
      try {
        if (selectedApp?._id) {
          const updateResponse = await updateDraftingHistoryDocumentApi(
            "contract",
            {
              _id: selectedApp._id,
              user_id: userInfo._id,
              Document: html,
              Title: title,
              form_metadata: generatedContract.form_metadata,
            }
          );
          savedHistoryId = updateResponse?.data?.data?._id ?? selectedApp._id;
        } else {
          const saveResponse = await saveDraftingHistoryApi("contract", {
            Category: contractName,
            timecreated: currentTime,
            chat_id: chatId,
            user_id: userInfo._id,
            Title: title,
            Document: html,
            form_metadata: generatedContract.form_metadata,
          });
          savedHistoryId = saveResponse?.data?.data?._id;
        }
      } catch (saveError) {
        console.warn("Failed to save contract drafting history.", saveError);
      }

      setGeneratedContract(generatedContract);
      dispatch({
        type: "SET_SELECTED_CONTRACT",
        payload: {
          ...generatedContract.metadata,
          _id: savedHistoryId,
          Title: title,
          Category: contractName,
          chat_id: chatId,
          form_metadata: generatedContract.form_metadata,
        },
      });
      setIsGenerating(false);
      setReadOnly(true);
      dispatch(getContractTitles('contract', userInfo?._id));
      // Deduct credits
      const deductionSuccess = deductCredits(
        {
          userId,
          creditsToDeduct: 1,
          event: "Generate contract on contract drafting page",
        },
        dispatch
      );

      if (!deductionSuccess) {
        console.warn(
          "Failed to deduct credits. Ensure retry or user notification."
        );
      }
      setReadOnly(true);
      setIsEditing(false);
    } catch (error) {
      toast.error(
        "The contract could not be saved. Something went wrong."
      );
      setIsGenerating(false);
      setReadOnly(false);
      console.log(error);
    } finally {
      setIsGenerating(false);
    }
  };

  const getContractTypes = async () => {
    setIsLoadingTypes(true);
    try {
      const response = await getContractTypesApi();
      if (response.status === 200) {
        setIsLoadingTypes(false);
        const contractTypesData = response.data;

        const updatedContractTypes = Object.entries(
          contractTypesData?.forms || {}
        ).map(([endpoint, name]) => ({
          Name: name,
          Endpoint: endpoint,
        }));

        const sortedContractTypes = updatedContractTypes.sort((a, b) =>
          a.Name.localeCompare(b.Name)
        );

        setContractTypes(sortedContractTypes);
      }
    } catch (error) {
      setIsLoadingTypes(false);
      console.log(error);
    }
  };

  const handleLoadTemplate = async (value) => {
    // if (!selectedContractType) return;
    setIsFormLoading(true);
    try {
      const response = await getDocumentSchemaApi(value.Name);
      if (response.status === 200) {
        const schema = response.data;
        setFormConfig({ form: { id: schema.title, fields: schema.fields } });
        setIsFormLoading(false);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleRadioChange = (e, fieldName) => {
    setFormData((prevState) => ({
      ...prevState,
      [fieldName]: e.target.value,
    }));
  };

  const validateFields = (fields, formData) => {
    const missingFields = [];

    const isRequired = (field) =>
      field.required === true || field.required === "true";

    fields.forEach((field) => {
      if (field.type === "list" && Array.isArray(field.fields)) {
        // Recursively validate nested fields in case of "list"
        field.fields.forEach((nestedField) => {
          if (isRequired(nestedField) && !formData[nestedField.name]) {
            missingFields.push(nestedField);
          }
        });
        // const nestedMissing = validateFields(field.fields, formData);
      } else if (isRequired(field) && !formData[field.name]) {
        // Validate non-list fields
        missingFields.push(field);
      }
    });

    return missingFields;
  };

  const generateContract = async (regenerate) => {
    // Validate required fields
    const missingFields = validateFields(
      formConfig?.form?.fields || [],
      formData
    );
    if (missingFields?.length > 0) {
      toast.info("Please fill all the required fields.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      return;
    } else {
      if (regenerate) {
        setOpenConfirmationModal(true);
      } else {
        setOpenTitleInputModal(true);
      }
    }
  };

  const handleSelectTemplate = (value) => {
    setSelectedContractType(value || "");
    setReadOnly(false);
    setGeneratedContract("");
    setTempChatId("");
    setFormData({});
    if (!value) {
      setFormConfig({});
      return;
    }
    handleLoadTemplate(value);
  };

  useEffect(() => {
    if (isClicked) {
      setSelectedContractType("");
      setFormConfig({});
      setFormData({});
      dispatch({ type: "NEW_CONTRACT_RESET" });
      setReadOnly(false);
      setIsEditing(false);
    }
  }, [isClicked, dispatch]);

  useEffect(() => {
    getContractTypes();
  }, []);

  useEffect(() => {
    if (selectedApp) {
      setFormConfig({ form: selectedApp?.form_metadata });
      setTempChatId("");
      setReadOnly(true);
    }

    if (selectedApp?.form_metadata) {
      const initialData = initializeFormData(
        selectedApp?.form_metadata?.fields
      );
      setFormData(initialData);
    }
  }, [selectedApp]);

  return (
    <>
      <Helmet>
        <title>LawGPT - Contract Drafting</title>
      </Helmet>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          ...(isMobileView
            ? { width: "100%" }
            : {
                flexBasis: isCollapsed ? "35%" : "29%",
                flexShrink: 0,
                transition: "flex-basis 0.3s ease",
              }),
          height: "100%",
          boxSizing: "border-box",
          margin: "12px",
          backgroundColor: "#f5f5f5",
          borderRadius: "12px",
          border: "1px solid #F1F5F9",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
          overflow: "hidden",
          fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            boxSizing: "border-box",
            backgroundColor: "#FFFFFF",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              padding: "20px 24px",
              borderBottom: "1px solid #F1F5F9",
              backgroundColor: "#FFFFFF",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "16px",
            }}
          >
            <IconButton
              size="small"
                sx={{
                  display: { xs: "flex", lg: "none" },
                  width: "40px",
                  height: "40px",
                  borderRadius: "10px",
                  backgroundColor: "#F0F9FF",
                  color: "#0073A6",
                  "&:hover": {
                    backgroundColor: "#E0F2FE",
                  },
                }}
              onClick={toggleSidebar}
            >
                <FontAwesomeIcon icon={faBarsStaggered} fontSize="16px" />
            </IconButton>
                              <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "40px",
                      height: "40px",
                      backgroundColor: isEditing ? "#F0F9FF" : "#F3E8FF",
                      borderRadius: "12px",
                      color: isEditing ? "#0073A6" : "#9333EA",
                    }}
                  >
                    {isEditing ? (
                      <FileText size={20} />
                    ) : (
                      <Palette size={20} />
                    )}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "18px",
                      fontWeight: "600",
                      color: isEditing ? "#12243C" : "#12243C",
                      fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                    }}
                  >
                    {isEditing
                      ? "Edit Template"
                      : selectedApp
                      ? "Preview Template"
                      : "Select Template"}
                  </Typography>
                </Box>
            </Box>
          </Box>
          {/* Template Selection */}
          {!selectedApp && (
            <Box
              sx={{
                padding: "20px 20px 5px 20px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Searchable Dropdown */}
              <TemplateSearchDropdown
                label="Select a Template"
                options={contractTypes}
                value={selectedContractType}
                onChange={(value) => handleSelectTemplate(value)}
                loading={isLoadingTypes}
                disabled={isLoadingTypes}
              />
            </Box>
          )}
          {!isFormLoading &&
          selectedContractType &&
          Object.keys(selectedContractType).length > 0 &&
          Object.keys(formConfig).length !== 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                overflowY: "auto",
                padding: "20px 24px",
                "::-webkit-scrollbar": {
                  width: "6px",
                },
                "::-webkit-scrollbar-track": {
                  background: "#F8FAFC",
                },
                "::-webkit-scrollbar-thumb": {
                  background: "#CBD5E1",
                  borderRadius: "8px",
                  "&:hover": {
                    background: "#94A3B8",
                  },
                },
              }}
            >
              <Typography
                sx={{
                  color: "#12243C",
                  marginTop: "2px",
                  marginBottom: "16px",
                  fontSize: "16px",
                  fontWeight: "600",
                  fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
              >
                {selectedApp?.Category || selectedContractType?.Name}
              </Typography>

              <DynamicForm
                formConfig={formConfig}
                formData={formData}
                readOnly={readOnly}
                handleInputChange={handleInputChange}
                handleRadioChange={handleRadioChange}
              />

              <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: "24px" }}>
                <Button
                  variant="contained"
                  endIcon={
                    isGenerating ? (
                      <CircularProgress size={18} sx={{ color: "#fff" }} />
                    ) : (
                      <Send size={18} />
                    )
                  }
                  disabled={isGenerating || readOnly}
                  sx={{
                    background: "linear-gradient(135deg, #0073A6 0%, #00C4F0 100%)",
                    color: "#FFFFFF",
                    textTransform: "none",
                    fontSize: "14px",
                    fontWeight: "500",
                    borderRadius: "10px",
                    padding: "10px 24px",
                    fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                    "&:hover": {
                      background: "linear-gradient(135deg, #005a85 0%, #0099cc 100%)",
                      boxShadow: "0 4px 12px rgba(0, 115, 166, 0.3)",
                    },
                    transition: "all 0.2s ease-in-out",
                  }}
                  onClick={() => {
                    generateContract(isEditing ? true : false);
                  }}
                >
                  {isEditing ? "Regenerate" : "Generate"}
                </Button>
              </Box>
            </Box>
          ) : isFormLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "40px 20px",
                flex: 1,
              }}
            >
              <CircularProgress size={24} sx={{ color: "#0073A6" }} />
            </Box>
          ) : selectedApp ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                overflowY: "auto",
                padding: "20px 24px",
                "::-webkit-scrollbar": {
                  width: "6px",
                },
                "::-webkit-scrollbar-track": {
                  background: "#F8FAFC",
                },
                "::-webkit-scrollbar-thumb": {
                  background: "#CBD5E1",
                  borderRadius: "8px",
                  "&:hover": {
                    background: "#94A3B8",
                  },
                },
              }}
            >
              <Typography
                sx={{
                  color: "#12243C",
                  marginTop: "2px",
                  marginBottom: "16px",
                  fontSize: "16px",
                  fontWeight: "600",
                  fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
              >
                {selectedApp?.Category || selectedContractType?.Name}
              </Typography>
              <DynamicForm
                formConfig={formConfig}
                readOnly={readOnly}
                formData={formData}
                handleInputChange={handleInputChange}
                handleRadioChange={handleRadioChange}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: "24px" }}>
                <Button
                  variant="contained"
                  endIcon={
                    isGenerating ? (
                      <CircularProgress size={18} sx={{ color: "#fff" }} />
                    ) : (
                      <Send size={18} />
                    )
                  }
                  disabled={isGenerating || readOnly}
                  sx={{
                    background: "linear-gradient(135deg, #0073A6 0%, #00C4F0 100%)",
                    color: "#FFFFFF",
                    textTransform: "none",
                    fontSize: "14px",
                    fontWeight: "500",
                    borderRadius: "10px",
                    padding: "10px 24px",
                    fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                    "&:hover": {
                      background: "linear-gradient(135deg, #005a85 0%, #0099cc 100%)",
                      boxShadow: "0 4px 12px rgba(0, 115, 166, 0.3)",
                    },
                    transition: "all 0.2s ease-in-out",
                  }}
                  onClick={() => {
                    generateContract(true);
                  }}
                >
                  Regenerate
                </Button>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                padding: "40px 20px",
                gap: "16px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "80px",
                  height: "80px",
                  borderRadius: "16px",
                  backgroundColor: "#F0F9FF",
                  color: "#0073A6",
                }}
              >
                <FileText size={40} />
              </Box>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#12243C",
                  fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                  textAlign: "center",
                }}
              >
                Select a Template
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#64748B",
                  fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                  textAlign: "center",
                  maxWidth: "300px",
                  lineHeight: "1.5",
                }}
              >
                Choose a template from the sidebar to begin drafting your contract
              </Typography>
            </Box>
          )}
        </Box>

        <TitleInputDialog
          open={openTitleInputModal}
          onClose={() => {
            setOpenTitleInputModal(false);
          }}
          onSave={(title) =>
            handleSaveTitleInput(title, false, isEditing ? true : false)
          }
        />
        <RegenerateConfirmationDialog
          open={openConfirmationModal}
          onClose={() => setOpenConfirmationModal(false)}
          onConfirm={() => {
            handleSaveTitleInput(
              selectedApp ? selectedApp?.Title : "",
              true,
              false
            );
            setOpenConfirmationModal(false);
            setIsEditing(false);
          }}
          type={"contract"}
        />
      </Box>
    </>
  );
};

export default ContractForm;
